"use client";

import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { ProductDocument } from "@/prismicio-types";
import { StoreProduct } from "@medusajs/types";
import { cn, extractKeyTextValue } from "@/lib/utils";
import { useState } from "react";
import { ProductImage } from "@/types";
import { __ } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";

export type ImageGalleryProps = {
  images: ProductImage[];
  locale: Locales;
  className?: string;
  isImageFullWidth?: boolean;
  onlyDisplayFirstImage?: boolean;
  collectionProducts?: ProductDocument[] | null;
  medusaProducts?: Record<string, StoreProduct> | undefined;
};

export const sanitizeImageUrl = (url: string): string => {
  if (!url || url.startsWith("https://images.prismic.io/")) {
    return url;
  }

  try {
    const decodedUrl = decodeURIComponent(url);

    const urlParts = decodedUrl.split("/");

    if (urlParts.length > 0) {
      const lastPartIndex = urlParts.length - 1;
      urlParts[lastPartIndex] = encodeURIComponent(urlParts[lastPartIndex]);
      return urlParts.join("/");
    }

    return encodeURI(decodedUrl);
  } catch (error) {
    console.warn("URL encoding error:", error);
    return encodeURI(url);
  }
};

const ImageGallery = ({
  images,
  className,
  isImageFullWidth = false,
  locale,
  onlyDisplayFirstImage = false,
  collectionProducts,
  medusaProducts,
}: ImageGalleryProps) => {
  const searchParams = useSearchParams();
  const validImages = images.filter((img) => !img.url.startsWith("http://localhost"));
  const sortedImages = [...validImages].sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
  const selectedCollectionProductId = searchParams.get("product");
  const selectedCollectionProduct = selectedCollectionProductId
    ? collectionProducts?.find((product) => extractKeyTextValue(product.data.medusaId) === selectedCollectionProductId)
    : undefined;

  const preferredSelectedImageUrl = selectedCollectionProductId
    ? medusaProducts?.[selectedCollectionProductId]?.images?.[0]?.url ||
      selectedCollectionProduct?.data.images?.[0]?.image?.url ||
      selectedCollectionProduct?.data.featuredImage?.url
    : undefined;
  const preferredSelectedImageAlt =
    selectedCollectionProduct?.data.images?.[0]?.image?.alt ||
    selectedCollectionProduct?.data.featuredImage?.alt ||
    selectedCollectionProduct?.data.title ||
    __("Product image", locale);

  const selectedCollectionImageIndex = preferredSelectedImageUrl
    ? sortedImages.findIndex((image) => sanitizeImageUrl(image.url) === sanitizeImageUrl(preferredSelectedImageUrl))
    : -1;
  const [manualSelection, setManualSelection] = useState<{ index: number; productId: string | null } | null>(null);
  const activeIndex =
    manualSelection?.productId === selectedCollectionProductId
      ? Math.min(manualSelection.index, Math.max(sortedImages.length - 1, 0))
      : selectedCollectionImageIndex >= 0
        ? selectedCollectionImageIndex
        : 0;
  const activeImage =
    manualSelection?.productId === selectedCollectionProductId
      ? sortedImages[activeIndex]
      : selectedCollectionImageIndex >= 0
        ? sortedImages[selectedCollectionImageIndex]
        : preferredSelectedImageUrl
          ? {
              url: preferredSelectedImageUrl,
              alt: preferredSelectedImageAlt,
              source: preferredSelectedImageUrl.startsWith("https://images.prismic.io/") ? ("prismic" as const) : ("medusa" as const),
              isFeatured: false,
            }
          : sortedImages[activeIndex];

  return (
    <aside
      className={cn(
        "bg-product-card relative flex aspect-square h-full max-h-96 w-full max-w-[22.063rem] items-center justify-center overflow-hidden rounded-md p-3 sm:max-w-[761px] lg:max-h-[40.75rem]",
        className
      )}
    >
      {!onlyDisplayFirstImage && (
        <ul
          aria-label="Product image thumbnails"
          className="absolute top-3 left-3 z-20 hidden flex-col gap-1.25 sm:flex"
        >
          {sortedImages.slice(0, 5).map((image, index) => (
            <li key={index}>
              <button
                type="button"
                onClick={() => setManualSelection({ index, productId: selectedCollectionProductId })}
                className={cn(
                  "flex size-[3.438rem] cursor-pointer items-center justify-center overflow-hidden rounded-sm bg-white focus-visible:opacity-100",
                  "transition-all duration-200 hover:opacity-100",
                  activeIndex === index ? "opacity-100" : "opacity-50",
                  isImageFullWidth && "bg-white/80"
                )}
                aria-label={`Thumbnail ${index + 1} - ${image.alt}`}
                aria-current={activeIndex === index}
              >
                <figure className="relative aspect-square size-10 overflow-hidden rounded-sm">
                  <Image
                    src={image.source === "medusa" ? sanitizeImageUrl(image.url) : image.url}
                    alt={image.alt}
                    fill
                    sizes="2.5rem"
                    className={cn(
                      "absolute size-full object-contain mix-blend-multiply",
                      isImageFullWidth && "object-cover"
                    )}
                  />
                </figure>
              </button>
            </li>
          ))}
        </ul>
      )}

      {activeImage ? (
        <figure
          aria-live="polite"
          aria-atomic="true"
          className={cn(
            "aspect-square h-full w-full",
            isImageFullWidth ? "" : "relative max-h-56 max-w-56 md:max-h-80 md:max-w-80 xl:max-h-96 xl:max-w-96"
          )}
        >
          <Image
            src={activeImage.source === "medusa" ? sanitizeImageUrl(activeImage.url) : activeImage.url}
            alt={activeImage.alt}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority
            className={cn("object-contain mix-blend-multiply", isImageFullWidth && "object-cover")}
          />
        </figure>
      ) : (
        <div className="opacity-50">{__("No Image", locale)}</div>
      )}
    </aside>
  );
};

export default ImageGallery;
